import { useEffect, useState } from "react";
import axios from "axios";
import { Button, Spinner } from "react-bootstrap";
import type { EpicRunNoteData } from "../../types/epicLeaderboard";
import { analytics } from "../../utils/analytics";
import "./epicLeaderboard.css";

type NoteState =
  | { status: "loading" }
  | { status: "ok"; data: EpicRunNoteData }
  | { status: "missing" }
  | { status: "error" };

// Notes never change once posted, so each run's note is fetched at most once
// per page load. null = the API said there is no public note for that run.
const noteCache = new Map<number, EpicRunNoteData | null>();

const cachedState = (runId: number): NoteState | null => {
  if (!noteCache.has(runId)) return null;
  const hit = noteCache.get(runId);
  return hit ? { status: "ok", data: hit } : { status: "missing" };
};

// Fetches the note of a run the first time `enabled` turns on; later opens
// come from the cache.
const useRunNote = (runId: number, enabled: boolean) => {
  const [state, setState] = useState<NoteState>(
    () => cachedState(runId) ?? { status: "loading" }
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const hit = cachedState(runId);
    if (hit) {
      setState(hit);
      return undefined;
    }

    const controller = new AbortController();
    setState({ status: "loading" });
    axios
      .get<EpicRunNoteData>(
        `${process.env.REACT_APP_API}public/epic-leaderboard/runs/${runId}/note`,
        { signal: controller.signal }
      )
      .then((res) => {
        if (controller.signal.aborted) return;
        noteCache.set(runId, res.data);
        setState({ status: "ok", data: res.data });
      })
      .catch((err) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          noteCache.set(runId, null);
          setState({ status: "missing" });
          return;
        }
        console.error("Failed to fetch epic run note", err);
        analytics.trackError("epic_run_note", String(err?.message));
        setState({ status: "error" });
      });

    return () => controller.abort();
  }, [runId, enabled, attempt]);

  return { state, retry: () => setAttempt((n) => n + 1) };
};

type ToggleProps = {
  note: number;
  open: boolean;
  onToggle: () => void;
};

// The "Note #2555" button. Stateless so a table can keep one note open at a
// time and render the panel elsewhere (a full-width row).
export const EpicRunNoteToggle = ({ note, open, onToggle }: ToggleProps) => (
  <Button
    type="button"
    size="sm"
    variant="outline-light"
    className={`epic-lb-note-toggle${open ? " epic-lb-note-toggle--open" : ""}`}
    aria-expanded={open}
    onClick={onToggle}
  >
    Note #{note}
  </Button>
);

type PanelProps = {
  runId: number;
};

// The note itself. Mount it only while open: it fetches on mount. The body is
// plain text in a <pre>; never HTML.
export const EpicRunNotePanel = ({ runId }: PanelProps) => {
  const { state, retry } = useRunNote(runId, true);

  if (state.status === "loading") {
    return (
      <div className="epic-lb-note epic-lb-note--status">
        <Spinner animation="border" size="sm" variant="light" />
      </div>
    );
  }
  if (state.status === "missing") {
    return (
      <div className="epic-lb-note epic-lb-note--status epic-lb-muted">
        No public note for this run.
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="epic-lb-note epic-lb-note--status">
        <span className="me-2">Couldn't load the note.</span>
        <Button size="sm" variant="outline-light" onClick={retry}>
          Retry
        </Button>
      </div>
    );
  }

  const { author, subject, dateEt, body } = state.data;
  const heading = [author, subject].filter(Boolean).join(" - ");
  return (
    <div className="epic-lb-note">
      {(heading || dateEt) && (
        <div className="epic-lb-note-header">
          <span>{heading}</span>
          {dateEt && <span className="epic-lb-muted">{dateEt}</span>}
        </div>
      )}
      <pre className="epic-lb-note-body">{body}</pre>
    </div>
  );
};

type Props = {
  runId: number;
  note: number;
};

// Self-contained toggle + panel, for places where the note can open in place
// (stat tile, player cards).
const EpicRunNote = ({ runId, note }: Props) => {
  const [open, setOpen] = useState(false);

  const toggle = () => {
    if (!open) {
      analytics.trackFeatureUsage("EpicLeaderboard", "note_open", { runId });
    }
    setOpen(!open);
  };

  return (
    <div className="epic-lb-note-wrap">
      <EpicRunNoteToggle note={note} open={open} onToggle={toggle} />
      {open && <EpicRunNotePanel runId={runId} />}
    </div>
  );
};

export default EpicRunNote;
